/**
 * Exception di applicazione.
 * Questa classe crea un oggetto ApplicationError per standardizzare ed estendere le eccezioni.
 *
 * Per utilizzarlo in un componente:
 * 1. Importarlo
 * 2. Per utilizzarlo usare throw new ApplicationError('exception message');
 */

import { ErrorHandler, Injectable, Injector, inject } from "@angular/core";
import { LoggerService } from "./logger.service";
import { TranslateService } from "src/app/shared/util/translate.service";
import { ApplicationHttpResponse } from "sharedclasses";
import { Router } from "@angular/router";
import { UrlCollection } from "src/app/app-routing.module";
import { ToastService } from "src/app/shared/services/toast.service";
import { TokenStorageService } from "src/app/global-service/token-storage.service";
import { clearClientSession } from "src/app/global-service/session-cleanup";
import { EnvironmentLoaderService } from "./env-config.service";
// import { OopsToastService } from "src/app/shared/components/oops-toast/oops-toast.service";


export class HTTPError extends Error {
    /** Nome dell'errore usato come chiave nell'i18n */
    public errorKey?: string;

    constructor(err?: string) {
        super();

        this.errorKey = err;
    }
}

export class ClientError extends Error {
    /** Codice di errore */
    public httpErrorCode?: number;
    /** Descrizione errore */
    public applicationError?: string;
    /** Id errore */
    public messageFromError?: Record<string, unknown> | string;

    constructor(applicationError: ApplicationHttpResponse<unknown>) {
        super();

        this.applicationError = applicationError?.error;
        this.httpErrorCode = applicationError?.httpErrorCode;
        this.messageFromError = applicationError?.messageFromError;
    }
}

/** Valore `applicationError` quando la sessione non è valida e si reindirizza al login (nessun toast in {@link GlobalError}). */
export const APPLICATION_ERROR_AUTH_REDIRECT = "AUTH_REDIRECT" as const;

/**
 * Codici applicativi che significano "credenziale assente o non valida".
 *
 * `AUTH_TOKEN_INVALID` copre quattro casi distinti del backend: token inesistente, token scaduto e
 * **tutti e tre i rami del controllo CSRF** (header assente, valore lato server assente, confronto
 * negativo). Erano proprio quei tre a mancare: la pulizia scattava su un elenco di tre *messaggi*,
 * e i messaggi del CSRF non erano fra quelli. Un utente la cui sessione perdeva il CSRF lato
 * server riceveva 403 su ogni chiamata e non veniva riportato al login: restava in
 * un'applicazione che non funzionava, senza capire perché.
 */
const SESSION_INVALID_APPLICATION_ERRORS: readonly string[] = ["AUTH_TOKEN_MISSING", "AUTH_TOKEN_INVALID"];

/** Messaggio di `jsonwebtoken` che affiora dai percorsi che non passano dal decoratore di auth. */
const JWT_MISSING_MESSAGE = "jwt must be provided";

/**
 * `true` se l'errore dice che la sessione non è più usabile, e quindi va ripulita.
 *
 * La decisione è sul **codice**, non sul testo: il codice è il contratto, il messaggio è
 * diagnostica e cambia. Due cose che il codice HTTP da solo non distingue, e per cui serve anche
 * `applicationError`:
 *
 * - un **401 `LOGIN_FAILED`** è la risposta di `POST /login` a una password sbagliata. Trattarlo
 *   come sessione invalida farebbe scattare pulizia e redirect a ogni errore di digitazione nella
 *   pagina di accesso;
 * - un **403 `NOT_AUTHORIZED`** è autorizzazione (scope insufficiente): va alla pagina `/403` e
 *   non deve toccare la sessione. Il `NOT_AUTHORIZED` con **401** è invece `@TenantCheck`, cioè
 *   una sessione che non vale per questo tenant, e quello sì va ripulito.
 * @param fields Campi dell'errore applicativo così come arrivano dal backend
 * @returns true se la sessione va considerata non valida
 */
export function isSessionInvalidError(fields: { applicationError?: string, httpErrorCode?: number, messageFromError?: Record<string, unknown> | string }): boolean {
  if (fields.applicationError && SESSION_INVALID_APPLICATION_ERRORS.includes(fields.applicationError)) {
    return true;
  }
  if (fields.httpErrorCode === 401 && fields.applicationError === "NOT_AUTHORIZED") {
    return true;
  }
  return typeof fields.messageFromError === "string" && fields.messageFromError.endsWith(JWT_MISSING_MESSAGE);
}

export class ApplicationError extends Error {
    /** Descrizione errore */
    public applicationError?: string;
    /** Codice di errore */
    public httpErrorCode?: number;
    /** Id errore */
    public messageFromError?: Record<string, unknown> | string;
    /** Nome dell'errore usato come chiave nell'i18n */
    public errorKey?: string;
    /** Nome del servizio dalla quale proviene l'errore */
    public serviceName: string;
    /** Nome della funzione o dell’operazione da cui proviene l’errore */
    public method: string;

    constructor(error: unknown, method: string, serviceName: string) {
        super();

        this.method = method;
        this.serviceName = serviceName;

        if (error instanceof ClientError) {
            this.applicationError = error.applicationError;
            this.httpErrorCode = error.httpErrorCode;
            this.messageFromError = error.messageFromError;
        }
        if (error instanceof HTTPError) {
            this.errorKey = error.errorKey;
        }
    }
}

@Injectable()
export class GlobalError implements ErrorHandler {
    private injector = inject(Injector);
    private toast = inject(ToastService);

    constructor() { }

    /**
     *
     * Gestore centralizzato degli errori
     *
     * @param error
     */
    public handleError(err: ApplicationError): void {
        const logger = this.injector.get(LoggerService);
        // Il router ha già avviato la navigazione verso il login: nessun toast da mostrare.
        if (err.applicationError === APPLICATION_ERROR_AUTH_REDIRECT) {
            logger.debug(`AUTH_REDIRECT: navigazione login già avviata, nessun toast. ${err.message ?? ""}`);
            return;
        }

        const translate = this.injector.get(TranslateService);
        const router = this.injector.get(Router);

        if (isSessionInvalidError(err)) {
            this.handleSessionNotValid(logger, router);
            return;
        }

        // Qui arriva solo il 403 di AUTORIZZAZIONE: la sessione è valida, l'operazione no.
        if (err.httpErrorCode == 403) {
            void router.navigate([UrlCollection.forbidden]);
            return;
        }

        this.handleTranslatedApplicationErrors(logger, translate, err);
    }

    /**
     * Toast e log per errori con chiavi i18n (codice HTTP, applicationError o errorKey).
     */
    private handleTranslatedApplicationErrors(logger: LoggerService, translate: TranslateService, err: ApplicationError): void {
        const errorPrefix = "errors.";
        const { httpErrorCode, errorKey, messageFromError, applicationError, method, serviceName, name } = err;
        const hasErrorCode = translate.has(httpErrorCode?.toString());
        const hasApplicationError = translate.has(errorPrefix + applicationError);
        const hasHttpError = translate.has(errorKey);
        const appendix = `From ${serviceName} -- Method ${method?.toUpperCase()}`;

        if (hasErrorCode || hasApplicationError) {
            this.toastAndLogCodeOrApplicationError(translate, logger, appendix, {
                errorPrefix,
                hasApplicationError,
                applicationError,
                httpErrorCode,
                messageFromError
            });
        }

        if (hasHttpError && errorKey) {
            const translatedError = translate.instant<string>(errorPrefix + errorKey);
            this.toast.error(translatedError);
            logger.error(translatedError, appendix);
        }

        if (!hasHttpError && !hasErrorCode && !hasApplicationError) {
            this.toast.error(name);
            logger.error(name, appendix);
        }
    }

    private toastAndLogCodeOrApplicationError(
        translate: TranslateService,
        logger: LoggerService,
        appendix: string,
        ctx: {
            errorPrefix: string;
            hasApplicationError: boolean;
            applicationError?: string;
            httpErrorCode?: number;
            messageFromError?: Record<string, unknown> | string;
        }
    ): void {
        const param = this.formatMessageFromErrorParam(ctx.messageFromError);
        const translationKey =
            ctx.applicationError && ctx.hasApplicationError
                ? ctx.errorPrefix + ctx.applicationError
                : ctx.httpErrorCode?.toString() ?? "";
        const translatedError = translate.instant<string>(translationKey, { arg: { param } });
        const toastMessage =
            translatedError && translatedError.length > 0
                ? translatedError
                : translate.instant<string>("generic.ERROR_MESSAGE");
        this.toast.error(toastMessage);
        logger.error(translatedError, appendix);
    }

    private formatMessageFromErrorParam(messageFromError?: Record<string, unknown> | string): string {
        if (typeof messageFromError === "string") {
            return messageFromError;
        }
        return Object.values(messageFromError ?? []).join(", ");
    }

    /**
     * Gestisce una sessione non più valida (token assente, non valido, scaduto, CSRF non valido, o
     * non valida per il tenant della richiesta): ripulisce lo stato locale e riporta al login.
     *
     * Ripuliva il solo token, lasciando in `localStorage` le chiavi che accompagnano una sessione:
     * ora la pulizia è una sola per tutta l'applicazione ({@link clearClientSession}).
     *
     * @param logger - Servizio di logging per tracciare l'evento
     * @param router - Router per effettuare il reindirizzamento
     */
    private handleSessionNotValid(logger: LoggerService, router: Router): void {
        try {
            const tokenStorage = this.injector.get(TokenStorageService);
            const envConfig = this.injector.get(EnvironmentLoaderService);
            const env = envConfig.getEnvConfig();

            // Determina la chiave del token basata sul tipo di login
            const tokenKey = env.loginType === "local" ? env.localTokenKey : env.ssoTokenKey;

            logger.warn("Session is no longer valid: clearing the local state and redirecting to the login page");
            clearClientSession(tokenStorage, tokenKey);

            // Reindirizza alla pagina di login
            void router.navigate([UrlCollection.localAuthentication]);

        } catch (cleanupError) {
            void router.navigate([UrlCollection.localAuthentication]);
        }
    }
}
